import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, ContentTile } from '@unmind/design-system';
import SectionError from 'Shared/SectionError';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useAssetToken } from '../hooks/useAssetToken';
import { useDailyBoost } from '../hooks/useDailyBoost';

/** Today's Daily Boost short, rendered as a hero banner. */
const DailyBoostBanner = () => {
  const { t: translate } = useTranslation('homepage');
  const { assetToken } = useAssetToken();
  const {
    tool,
    trackShortClick,
    to,
    ctaText,
    durationText,
    toolSummary,
    isVideo,
    loading,
    error,
    refetch,
  } = useDailyBoost('daily-boost-hero-banner', { logSectionErrors: true });

  if (loading && !tool) {
    return <HomeSectionSkeleton testId="daily-boost-skeleton" tileCount={1} />;
  }

  if (error && !tool) {
    // `errorPolicy: 'none'` makes refetch() reject on failure; the hook's own
    // `data` and `error` still drive what renders.
    return (
      <SectionError onRetry={async () => refetch().catch(() => undefined)} />
    );
  }

  if (!tool) {
    return null;
  }

  const { title, mediaType, estimatedMins, cloudinaryThumbnail } = tool;

  return (
    <section>
      <h2 className="title-s section-title">
        {translate('daily_boost_banner.title')}
      </h2>
      <ContentTile
        variant="hero"
        styling="enclosed"
        to={to}
        onClick={trackShortClick}
        actions={[
          <Button
            key="cta"
            asChild
            variant="primary"
            label={ctaText}
            icon="arrow-right"
          >
            <Link to={to} />
          </Button>,
        ]}
        item={{
          itemType: (mediaType?.value ?? 'audio') as 'audio' | 'video',
          title,
          description: toolSummary as string | undefined,
          thumbnailUrl:
            cloudinaryThumbnail?.path && assetToken
              ? `${cloudinaryThumbnail.path}?${assetToken}`
              : undefined,
          fallbackIcon: isVideo ? 'video-on' : 'audio',
          tags: estimatedMins ? [durationText] : undefined,
        }}
      />
    </section>
  );
};

export default DailyBoostBanner;
